import { SITE } from "@/lib/routes";

export const absoluteUrl = (path: string) =>
  `${SITE.domain}${path.startsWith("/") ? path : `/${path}`}`;

/**
 * Standard dimensions for every Open Graph image on the site.
 * All files under /public/og/ are generated at this exact size
 * so social crawlers (Facebook, LinkedIn, Twitter/X, Slack) get
 * consistent previews.
 */
export const OG_IMAGE_WIDTH = 1536;
export const OG_IMAGE_HEIGHT = 1024;

/**
 * Default Open Graph image served when a route does not provide its own.
 * Keeps every page eligible for a rich social card.
 */
export const DEFAULT_OG_IMAGE = "/og/home.jpg";

/**
 * Convention: every route has a matching `/og/<slug>.jpg` generated
 * by scripts/generate-og-images.mjs. The slug flattens path separators
 * with hyphens, so `/foo/bar` -> `foo-bar`. Root maps to `home`.
 */
export function ogImageForPath(routePath: string): string {
  if (routePath === "/" || routePath === "") return DEFAULT_OG_IMAGE;
  const slug = routePath.replace(/^\/+|\/+$/g, "").replace(/\//g, "-");
  return `/og/${slug}.jpg`;
}

export type PageMetaInput = {
  title: string;
  description: string;
  path: string; // canonical, no trailing slash — e.g. "/" or "/barcode-reader"
  ogType?: "website" | "article" | "product";
  ogImage?: string; // absolute URL
  /**
   * Descriptive alt text for the OG/Twitter image. Required when
   * ogImage is set so the share preview is accessible.
   */
  ogImageAlt?: string;
  noindex?: boolean;
};

/**
 * Build the head() return value for a route. Always include this on every
 * public route. Sets canonical only on the leaf (root never sets canonical
 * — see TanStack links concat caveat).
 */
export function pageHead({
  title,
  description,
  path,
  ogType = "website",
  ogImage,
  ogImageAlt,
  noindex,
}: PageMetaInput) {
  // Every page gets an og:image. If the route doesn't pass one
  // explicitly, derive `/og/<slug>.jpg` from the path — every public
  // route has a matching image generated by scripts/generate-og-images.mjs.
  const resolvedImage = ogImage ?? absoluteUrl(ogImageForPath(path));
  const meta = [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: ogType },
    { property: "og:url", content: absoluteUrl(path) },
    { property: "og:site_name", content: `${SITE.name}.com` },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:image", content: resolvedImage },
    { property: "og:image:width", content: String(OG_IMAGE_WIDTH) },
    { property: "og:image:height", content: String(OG_IMAGE_HEIGHT) },
    { property: "og:image:type", content: "image/jpeg" },
    { name: "twitter:image", content: resolvedImage },
  ];
  if (ogImageAlt) {
    meta.push({ property: "og:image:alt", content: ogImageAlt });
    meta.push({ name: "twitter:image:alt", content: ogImageAlt });
  }
  if (noindex) {
    meta.push({ name: "robots", content: "noindex,nofollow" });
  }
  return {
    meta,
    links: [{ rel: "canonical", href: absoluteUrl(path) }],
  };
}

/* ---------- JSON-LD builders ---------- */

export const websiteJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: `${SITE.name}.com`,
  url: SITE.domain,
  description: SITE.tagline,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE.domain}/barcode-type-detector?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const organizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: `${SITE.name}.com`,
  url: SITE.domain,
  logo: `${SITE.domain}/favicon.svg`,
});

export const breadcrumbJsonLd = (items: { name: string; path: string }[], id?: string) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  ...(id ? { "@id": id } : {}),
  // Filter out empty crumbs so the JSON-LD matches the visible breadcrumb
  // trail exactly (the <Breadcrumbs> component applies the same filter).
  itemListElement: items
    .filter((item) => item.name?.trim() && item.path?.trim())
    .map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
});

/**
 * WebPage JSON-LD for content pages (Privacy, Terms, etc.). Includes
 * @id anchoring, isPartOf -> WebSite, publisher -> Organization, and
 * a reference to the page's BreadcrumbList by @id so search engines
 * can stitch the graph together.
 */
export const webPageJsonLd = (input: {
  path: string;
  title: string;
  description: string;
  /** ISO date (YYYY-MM-DD). */
  datePublished: string;
  /** ISO date (YYYY-MM-DD). */
  dateModified: string;
  /** Optional @id of an in-page BreadcrumbList to reference. */
  breadcrumbId?: string;
}) => {
  const pageId = `${absoluteUrl(input.path)}#webpage`;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": pageId,
    name: input.title,
    description: input.description,
    url: absoluteUrl(input.path),
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      "@id": `${SITE.domain}#website`,
      url: SITE.domain,
      name: `${SITE.name}.com`,
    },
    publisher: {
      "@type": "Organization",
      "@id": `${SITE.domain}#organization`,
      name: `${SITE.name}.com`,
      url: SITE.domain,
      logo: `${SITE.domain}/favicon.svg`,
    },
    ...(input.breadcrumbId ? { breadcrumb: { "@id": input.breadcrumbId } } : {}),
    datePublished: input.datePublished,
    dateModified: input.dateModified,
    mainEntityOfPage: { "@id": pageId },
  };
};

/**
 * ContactPage JSON-LD. Same publisher / isPartOf / breadcrumb graph as
 * webPageJsonLd, but typed for Schema.org's ContactPage so search
 * engines treat it as the contact endpoint for the Organization.
 */
export const contactPageJsonLd = (input: {
  path: string;
  title: string;
  description: string;
  breadcrumbId?: string;
}) => {
  const pageId = `${absoluteUrl(input.path)}#webpage`;
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": pageId,
    name: input.title,
    description: input.description,
    url: absoluteUrl(input.path),
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      "@id": `${SITE.domain}#website`,
      url: SITE.domain,
      name: `${SITE.name}.com`,
    },
    publisher: {
      "@type": "Organization",
      "@id": `${SITE.domain}#organization`,
      name: `${SITE.name}.com`,
      url: SITE.domain,
      logo: `${SITE.domain}/favicon.svg`,
    },
    ...(input.breadcrumbId ? { breadcrumb: { "@id": input.breadcrumbId } } : {}),
    mainEntityOfPage: { "@id": pageId },
  };
};

export const softwareAppJsonLd = (input: { name: string; description: string; path: string }) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: input.name,
  description: input.description,
  url: absoluteUrl(input.path),
  applicationCategory: "UtilitiesApplication",
  operatingSystem: "Any (Web)",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
});

export const ldScript = (data: object) => ({
  type: "application/ld+json",
  children: JSON.stringify(data),
});
